/**
 * Mojo Up AI Cloud's quotas (docs/plans/2026-10-mojo-up-cloud.md, "Quotas and abuse controls"): what one organisation may
 * hold and send on each plan, so a Free sign-up cannot crowd out a paying team on the same database.
 *
 * Cloud enforces these where each thing is created and answers a refusal in words naming the plan
 * and the limit: 402 `plan_limit` for something held (members, projects, bytes), 429 `rate_limited`
 * for a rate (requests or ingest records a minute). `null` is no limit and `0` is none at all (a
 * plan that does not include the thing); the two are never confused. A self-hosted Enterprise AIOE
 * applies none of them (its ingest rate is AIOE_INGEST_RECORDS_PER_MINUTE).
 *
 * The plans (JP, 1 Nov 2026): Free is an account for a person's own machines (sign-in, licence,
 * updates, remote control through the relay and the phone app) with no limit on their workbenches
 * and nodes, and nothing hosted: no projects, catalogue, memory, storage or backups. Teams is
 * everything shared, limited only by the seats bought and the storage pool.
 */
import { z } from "zod";
export const cloudQuotaPlanSchema = z.enum(["free", "teams", "enterprise"]);
export const cloudQuotaKeySchema = z.enum([
    "members",
    "ownedOrganisations",
    "projects",
    "devices",
    "nodes",
    "meshNodes",
    "catalogueEntries",
    "designSystems",
    "assetBytes",
    "ingestRecordsPerMinute",
    "backupBytes",
    "requestsPerMinute",
    "storageBytes",
    "exportBytes",
]);
/** A limit: a whole number, or null for none. */
const quotaLimitSchema = z.number().int().min(0).nullable();
export const cloudQuotasSchema = z.object({
    /** People in a team: members and open invitations together (billing's seats, when on, may hold a team to fewer). */
    members: quotaLimitSchema,
    /** Teams one person may own (their personal organisation is not counted); the highest plan among the teams they own decides. */
    ownedOrganisations: quotaLimitSchema,
    projects: quotaLimitSchema,
    /** Desktops enrolled (workbenches). */
    devices: quotaLimitSchema,
    /** Headless nodes enrolled. */
    nodes: quotaLimitSchema,
    /**
     * Machines on the organisation's mesh: workbenches and headless nodes registered to it (phones and
     * the platform's own gateway and relay nodes are not counted). Mojo Up AI Cloud offers no mesh (21 Oct 2026): its
     * image never mounts it, so 0 on both plans binds only a source checkout run with AIOE_MESH_CLOUD=on.
     */
    meshNodes: quotaLimitSchema,
    /** The organisation's catalogue: skills, roles, prompts, teams, tool servers and design systems. */
    catalogueEntries: quotaLimitSchema,
    /** Design systems in the catalogue (each is a catalogue entry as well). */
    designSystems: quotaLimitSchema,
    /** Design-system assets (fonts, logos, stylesheets) kept in the object store, every design system together. */
    assetBytes: quotaLimitSchema,
    /** Records (events, usage, audit, tasks) the organisation's machines may send in a minute. */
    ingestRecordsPerMinute: quotaLimitSchema,
    /** Encrypted backup storage per person in the organisation. */
    backupBytes: quotaLimitSchema,
    /** Signed-in requests a minute for the whole organisation (people and machines together). */
    requestsPerMinute: quotaLimitSchema,
    /**
     * Everything the organisation keeps, in bytes: backups, design-system files, exports, invoices, memory and the
     * logs (audit, flows, usage records, counted by rows at a fixed size). The Storage account (`storage/account.ts`)
     * adds it up hourly; a write that would pass it is refused. On Teams the figure here is per paid seat and the
     * organisation's limit is that times its seats (`cloud/quotas.ts`); bought GB units are not built yet.
     */
    storageBytes: quotaLimitSchema,
    /** The largest one organisation export may be. */
    exportBytes: quotaLimitSchema,
});
const MiB = 1024 * 1024;
const GiB = 1024 * MiB;
/** Storage a Teams organisation holds for each paid seat, pooled across the team (21 Oct 2026). */
export const CLOUD_STORAGE_PER_SEAT_BYTES = 2 * GiB;
/**
 * The defaults per plan. A deployment may override any of them with AIOE_CLOUD_QUOTAS (JSON, per plan).
 * Teams' `storageBytes` is the figure for one seat: the API multiplies it by the seats bought (and
 * `null` stays unlimited), so an override is per seat too. Rates are abuse protection, not advertised.
 */
export const CLOUD_QUOTA_DEFAULTS = {
    free: {
        members: 1,
        ownedOrganisations: 3,
        projects: 0,
        devices: null,
        nodes: null,
        meshNodes: 0,
        catalogueEntries: 0,
        designSystems: 0,
        assetBytes: 0,
        ingestRecordsPerMinute: 2_000,
        backupBytes: 0,
        requestsPerMinute: 600,
        storageBytes: 0,
        exportBytes: 256 * MiB,
    },
    teams: {
        members: null,
        ownedOrganisations: 10,
        projects: null,
        devices: null,
        nodes: null,
        meshNodes: 0,
        catalogueEntries: null,
        designSystems: null,
        assetBytes: null,
        ingestRecordsPerMinute: 20_000,
        backupBytes: null,
        requestsPerMinute: 6_000,
        storageBytes: CLOUD_STORAGE_PER_SEAT_BYTES,
        exportBytes: 2 * GiB,
    },
    enterprise: {
        members: null,
        ownedOrganisations: null,
        projects: null,
        devices: null,
        nodes: null,
        meshNodes: null,
        catalogueEntries: null,
        designSystems: null,
        assetBytes: null,
        ingestRecordsPerMinute: null,
        backupBytes: null,
        requestsPerMinute: null,
        storageBytes: null,
        exportBytes: null,
    },
};
/**
 * How long Cloud keeps what grows by itself, per plan: set by the plan, not the person. Raw usage
 * records (the daily rollups stay) and mesh connection logs. A self-hosted Enterprise sets its own
 * (usage retention in Settings, 400 days by default). Backups' retention is in `backups/plans.ts`
 * (Free 30 days; Teams the standard 24 hourly, 14 daily, 8 weekly and 12 monthly).
 */
export const CLOUD_RETENTION_DEFAULTS = {
    free: { usageDays: 30, flowDays: 7 },
    teams: { usageDays: 90, flowDays: 30 },
};
export const quotaUnitSchema = z.enum(["count", "bytes", "perMinute"]);
/** How each quota reads to a person: its name in a row, its unit, and whose it is. */
export const CLOUD_QUOTA_TEXT = {
    members: { label: "Members", unit: "count", scope: "organisation" },
    ownedOrganisations: { label: "Teams you own", unit: "count", scope: "person" },
    projects: { label: "Projects", unit: "count", scope: "organisation" },
    devices: { label: "Workbenches", unit: "count", scope: "organisation" },
    nodes: { label: "Nodes", unit: "count", scope: "organisation" },
    meshNodes: { label: "Machines on the mesh", unit: "count", scope: "organisation" },
    catalogueEntries: { label: "Catalogue entries", unit: "count", scope: "organisation" },
    designSystems: { label: "Design systems", unit: "count", scope: "organisation" },
    assetBytes: { label: "Design system files", unit: "bytes", scope: "organisation" },
    ingestRecordsPerMinute: { label: "Records from machines", unit: "perMinute", scope: "organisation" },
    backupBytes: { label: "Your backup storage", unit: "bytes", scope: "person" },
    requestsPerMinute: { label: "Requests", unit: "perMinute", scope: "organisation" },
    storageBytes: { label: "Total storage", unit: "bytes", scope: "organisation" },
    exportBytes: { label: "Largest export", unit: "bytes", scope: "organisation" },
};
export const CLOUD_PLAN_TEXT = { free: "Free", teams: "Teams", enterprise: "Enterprise" };
/** A count or an amount of bytes in words: "1,200", "100 MB", "2,000 a minute". */
export function quotaAmountText(unit, n) {
    if (unit === "bytes") {
        if (n >= GiB)
            return `${+(n / GiB).toFixed(n % GiB ? 1 : 0)} GB`;
        if (n >= MiB)
            return `${+(n / MiB).toFixed(n % MiB ? 1 : 0)} MB`;
        if (n >= 1024)
            return `${Math.round(n / 1024)} KB`;
        return `${n} bytes`;
    }
    const s = n.toLocaleString("en-AU");
    return unit === "perMinute" ? `${s} a minute` : s;
}
/** One row of an organisation's quotas: "Members 4 of 10 on Free". `used` is absent for a rate. */
export const cloudQuotaRowSchema = z.object({
    key: cloudQuotaKeySchema,
    label: z.string(),
    unit: quotaUnitSchema,
    /** Whose the figure is: the organisation's, or the person asking's (teams they own, their backups). */
    scope: z.enum(["organisation", "person"]),
    limit: quotaLimitSchema,
    used: z.number().int().min(0).optional(),
    /** A rate's count in the minute so far (requests or records); absent for something held. */
    thisMinute: z.number().int().min(0).optional(),
    /** When something held is at (or past) its limit: the plan's own sentence for it, and what to do. */
    atLimit: z.string().optional(),
});
/** `GET /cloud/v1/organisations/:id/quotas`: the plan's limits and where the organisation stands. */
export const cloudOrganisationQuotasSchema = z.object({
    organisationId: z.string(),
    plan: cloudQuotaPlanSchema,
    planLabel: z.string(),
    quotas: cloudQuotasSchema,
    rows: z.array(cloudQuotaRowSchema),
});
/** The body of a quota refusal: 402 `plan_limit` (something held) or 429 `rate_limited` (a rate). */
export const cloudQuotaRefusalSchema = z.object({
    error: z.enum(["plan_limit", "rate_limited"]),
    quota: cloudQuotaKeySchema,
    plan: cloudQuotaPlanSchema,
    limit: z.number().int(),
    used: z.number().int().optional(),
    message: z.string(),
});
/** Sign-up refusals (`/auth/v1` and organisation creation): why, in words the sign-in page shows as is. */
export const signUpRefusalCodeSchema = z.enum(["captcha_required", "captcha_failed", "email_domain_blocked", "sign_up_limit", "organisation_daily_limit"]);
//# sourceMappingURL=cloudQuotas.js.map